import { useCallback, useEffect, useState } from 'react';
import { getOrderDetail, editOrder } from '@/api/order';
import OrderItemsTable, {
  ensureSingleTrailingEmpty,
  isEmptyOrderItem,
  type OrderItem,
  type ProductPickPayload,
} from '@/pages/order/components/OrderItemsTable';
import ProductSelector, { type SelectableProduct } from '@/pages/order/components/ProductSelector';

interface EditOrderModalProps {
  isOpen: boolean;
  orderId: string;
  orderSn?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

function mapDetailItems(
  lists: Array<{
    goods_id?: number | string;
    spec_id?: number | string;
    id?: number | string;
    goods_name?: string;
    quantity?: number | string;
    price?: number | string;
    unit?: string;
    sku_name?: string;
    remark?: string;
    goods?: { m_pic?: string } | null;
  }> = [],
): OrderItem[] {
  return lists.map((row) => ({
    productId: String(row.goods_id || row.spec_id || row.id || ''),
    name: row.goods_name || '未命名商品',
    quantity: Number(row.quantity ?? 0) || 0,
    unitPrice: Number(row.price ?? 0) || 0,
    unit: row.unit || '件',
    specId: row.spec_id != null ? String(row.spec_id) : undefined,
    spec: row.sku_name || '',
    image: row.goods?.m_pic || '',
    itemRemark: row.remark || '',
  }));
}

export default function EditOrderModal({
  isOpen,
  orderId,
  orderSn = '',
  onClose,
  onSuccess,
}: EditOrderModalProps) {
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [editableItems, setEditableItems] = useState<OrderItem[]>([]);
  const [orderRemark, setOrderRemark] = useState('');
  const [meta, setMeta] = useState({ sn: '', department: '', orderPerson: '' });
  const [showProductSelector, setShowProductSelector] = useState(false);
  const [lastItemError, setLastItemError] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const loadDetail = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    setLoadError('');
    try {
      const res = await getOrderDetail(orderId);
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok || !res.data) {
        setLoadError(res.message || res.msg || '订单加载失败');
        setEditableItems([]);
        return;
      }
      const raw = res.data;
      setMeta({
        sn: String(raw.order_sn || raw.id || orderSn),
        department: String(raw.department?.title || raw.office?.title || ''),
        orderPerson: String(raw.account_name || ''),
      });
      setOrderRemark(String(raw.remark || ''));
      setEditableItems(ensureSingleTrailingEmpty(mapDetailItems(raw.lists || [])));
    } catch {
      setLoadError('订单加载失败，请稍后重试');
      setEditableItems([]);
      setOrderRemark('');
    } finally {
      setLoading(false);
    }
  }, [orderId, orderSn]);

  useEffect(() => {
    if (!isOpen) {
      setShowProductSelector(false);
      setLastItemError(false);
      setLoadError('');
      setEditableItems([]);
      setOrderRemark('');
      return;
    }
    void loadDetail();
  }, [isOpen, loadDetail]);

  const handleUpdateQuantity = (index: number, quantity: number) => {
    setEditableItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], quantity };
      return next;
    });
  };

  const handleUpdateRemark = (index: number, remark: string) => {
    setEditableItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], itemRemark: remark };
      return next;
    });
  };

  const handleUpdateSpec = (
    index: number,
    specId: string,
    price: number,
    model: string,
    unit?: string,
  ) => {
    const duplicated = editableItems.some(
      (item, i) => i !== index && !isEmptyOrderItem(item) && item.specId && item.specId === specId,
    );
    if (duplicated) {
      showToast('该商品规格已在列表中，同一规格只能使用一次', 'error');
      return;
    }
    setEditableItems((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        specId,
        unitPrice: price,
        spec: model,
        ...(unit ? { unit } : {}),
      };
      return next;
    });
    showToast(`规格已更新为「${model}」`);
  };

  const handleUpdateProduct = (index: number, product: ProductPickPayload) => {
    const duplicated = editableItems.some(
      (item, i) =>
        i !== index && !isEmptyOrderItem(item) && item.specId && item.specId === product.specId,
    );
    if (duplicated) {
      showToast('该商品规格已在列表中，同一规格只能使用一次', 'error');
      return;
    }
    setEditableItems((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        productId: product.productId,
        name: product.name,
        image: product.image || '',
        specId: product.specId,
        spec: product.spec,
        unitPrice: product.unitPrice,
        unit: product.unit,
        quantity: next[index].quantity > 0 ? next[index].quantity : 1,
        specOptions: product.specs || [],
      };
      return ensureSingleTrailingEmpty(next);
    });
    showToast(`已更换为「${product.name}」并联动规格`);
  };

  const handleDeleteItem = (index: number) => {
    const target = editableItems[index];
    const filledCount = editableItems.filter((item) => !isEmptyOrderItem(item)).length;
    if (target && !isEmptyOrderItem(target) && filledCount <= 1) {
      setLastItemError(true);
      window.setTimeout(() => setLastItemError(false), 3000);
      return;
    }
    setEditableItems((prev) => ensureSingleTrailingEmpty(prev.filter((_, i) => i !== index)));
  };

  const handleAddProduct = (product: SelectableProduct) => {
    const existingIndex = editableItems.findIndex(
      (item) =>
        !isEmptyOrderItem(item) &&
        (item.specId === product.id || (!item.specId && item.productId === product.goodsId)),
    );
    if (existingIndex >= 0) {
      setEditableItems((prev) => {
        const next = [...prev];
        next[existingIndex] = {
          ...next[existingIndex],
          quantity: next[existingIndex].quantity + 1,
        };
        return next;
      });
      showToast(`"${product.name}" 数量已 +1`);
      return;
    }

    const newItem: OrderItem = {
      productId: product.goodsId,
      name: product.name,
      quantity: 1,
      unitPrice: product.price,
      unit: product.unit,
      specId: product.id,
      spec: product.model || '',
      image: product.image || '',
      specOptions: [
        {
          id: product.id,
          name: product.model || '',
          price: product.price,
          unit: product.unit,
        },
      ],
    };

    setEditableItems((prev) => {
      const emptyIndex = prev.findIndex((item) => isEmptyOrderItem(item));
      const next =
        emptyIndex >= 0
          ? (() => {
              const copy = [...prev];
              copy[emptyIndex] = newItem;
              return copy;
            })()
          : [...prev, newItem];
      return ensureSingleTrailingEmpty(next);
    });
    showToast(`已添加 "${product.name}"`);
  };

  const handleSave = async () => {
    if (saving) return;
    const filledItems = editableItems.filter((item) => !isEmptyOrderItem(item));
    if (filledItems.length === 0) {
      setLastItemError(true);
      window.setTimeout(() => setLastItemError(false), 3000);
      showToast('请至少保留一件商品', 'error');
      return;
    }
    if (filledItems.some((item) => !item.productId || !item.specId)) {
      showToast('请完善商品与规格后再保存', 'error');
      return;
    }

    setSaving(true);
    try {
      const res = await editOrder({
        order_id: Number(orderId) || orderId,
        remark: orderRemark.trim(),
        lists: filledItems.map((item) => ({
          goods_id: Number(item.productId) || item.productId,
          spec_id: Number(item.specId) || item.specId!,
          quantity: item.quantity,
          remark: item.itemRemark?.trim() || '',
        })),
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(res.message || res.msg || '订单修改失败', 'error');
        return;
      }
      showToast(res.message || '订单修改成功');
      onSuccess?.();
      onClose();
    } catch {
      showToast('订单修改失败，请稍后重试', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const titleSn = meta.sn || orderSn || orderId;

  return (
    <>
      <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40 p-4">
        <div className="bg-background-50 rounded-xl w-full max-w-6xl max-h-[92vh] flex flex-col shadow-2xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-background-200 shrink-0">
            <div>
              <h3 className="text-sm font-semibold text-foreground-900">修改订单</h3>
              <p className="text-xs text-foreground-500 mt-0.5">
                订单号 {titleSn}
                {meta.department ? ` · ${meta.department}` : ''}
                {meta.orderPerson ? ` · ${meta.orderPerson}` : ''}
              </p>
            </div>
            <button
              type="button"
              disabled={saving}
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-foreground-700 hover:bg-background-100 transition-colors disabled:opacity-50"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
            {loading ? (
              <div className="py-16 flex flex-col items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin text-xl text-primary-500" />
                <p className="text-sm text-foreground-400">正在加载订单...</p>
              </div>
            ) : loadError ? (
              <div className="py-16 flex flex-col items-center justify-center gap-3">
                <p className="text-sm text-foreground-500">{loadError}</p>
                <button
                  type="button"
                  onClick={() => void loadDetail()}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  重新加载
                </button>
              </div>
            ) : (
              <OrderItemsTable
                items={editableItems}
                editMode
                onUpdateQuantity={handleUpdateQuantity}
                onUpdateSpec={handleUpdateSpec}
                onUpdateProduct={handleUpdateProduct}
                onUpdateRemark={handleUpdateRemark}
                onDeleteItem={handleDeleteItem}
                onAddItem={() => setShowProductSelector(true)}
                lastItemError={lastItemError}
                headerActions={
                  <button
                    type="button"
                    onClick={() => setShowProductSelector(true)}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md bg-background-100 hover:bg-background-200 text-foreground-700 border border-background-300 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-add-line" />
                    添加商品
                  </button>
                }
                footerExtra={
                  <div>
                    <label className="text-sm font-medium text-foreground-800 mb-2 block">
                      订单备注
                    </label>
                    <textarea
                      value={orderRemark}
                      disabled={saving}
                      onChange={(e) => setOrderRemark(e.target.value.slice(0, 255))}
                      rows={3}
                      maxLength={255}
                      placeholder="可填写订单备注（选填）"
                      className="w-full px-3 py-2.5 rounded-lg border border-background-300 text-sm outline-none resize-none transition-colors focus:border-primary-400 disabled:bg-background-100"
                    />
                    <p className="text-xs text-foreground-400 mt-1.5 text-right">
                      {orderRemark.length}/255
                    </p>
                  </div>
                }
              />
            )}
          </div>

          <div className="px-5 py-4 border-t border-background-200 flex items-center justify-end gap-2 shrink-0">
            <button
              type="button"
              disabled={saving}
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-background-300 text-foreground-600 text-sm hover:bg-background-100 transition-colors whitespace-nowrap disabled:opacity-50"
            >
              取消
            </button>
            <button
              type="button"
              disabled={saving || loading || !!loadError}
              onClick={() => void handleSave()}
              className="px-4 py-2 rounded-lg bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-medium transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
            >
              {saving ? (
                <i className="ri-loader-4-line animate-spin" />
              ) : (
                <i className="ri-save-line" />
              )}
              {saving ? '保存中...' : '保存修改'}
            </button>
          </div>
        </div>
      </div>

      <ProductSelector
        isOpen={showProductSelector}
        onClose={() => setShowProductSelector(false)}
        onSelect={handleAddProduct}
        existingProductIds={editableItems
          .map((item) => item.specId)
          .filter(Boolean) as string[]}
      />

      {toast && (
        <div
          className={`fixed top-4 left-1/2 -translate-x-1/2 z-[130] px-4 py-2.5 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2 ${
            toast.type === 'success'
              ? 'bg-accent-500 text-background-50'
              : 'bg-primary-500 text-background-50'
          }`}
        >
          <i
            className={
              toast.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'
            }
          />
          {toast.message}
        </div>
      )}
    </>
  );
}
